import React, { useState } from 'react';
import { useAppData } from './context/DataContext';
import { useAnalytics } from './context/AnalyticsContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Form,
  Input,
  Button,
  Upload,
  Typography,
  message,
  Card,
  Space,
  Divider
} from 'antd';
import { UploadOutlined, PictureOutlined, InfoCircleOutlined } from '@ant-design/icons';
import Navbar from './Navbar';
import Footer from './Footer';
import LocationSearchInput from './LocationSearchInput';
import { getEventLoc } from './information.service';
const { Title, Text } = Typography;
const { TextArea } = Input;

const secrets = require('./secrets.js');
const api = secrets.server_url;

function CreateOrganization() {
  const { loggedInEmail, setAppData } = useAppData();
  const analytics = useAnalytics();
  const navigate = useNavigate();
  
  const [form] = Form.useForm();
  const [profileImage, setProfileImage] = useState(null);
  const [bannerImage, setBannerImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [bannerImageUrl, setBannerImageUrl] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [profileImageLoading, setProfileImageLoading] = useState(false);
  const [bannerImageLoading, setBannerImageLoading] = useState(false);
  const [locationCoordinates, setLocationCoordinates] = useState(null);

  // Function to upload an image and get back URL
  const uploadImage = async (imageFile) => {
    try {
      // Create FormData object
      const formData = new FormData();
      formData.append('image', imageFile);

      // Make the request
      const response = await axios.post(`${api}uploadImage`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });

      return response.data.imageUrl;
    } catch (error) {
      console.error('Error uploading image:', error.response?.data?.message || error.message);
      throw error;
    }
  };

  // Handle profile image upload
  const handleProfileImageUpload = async (file) => {
    setProfileImageLoading(true);
    try {
      const url = await uploadImage(file);
      setProfileImageUrl(url);
      setProfileImage(file);
      message.success('Profile image uploaded successfully');
    } catch (error) {
      message.error('Failed to upload profile image');
    } finally {
      setProfileImageLoading(false);
    }
    return false; // Prevent default upload behavior
  };

  // Handle banner image upload
  const handleBannerImageUpload = async (file) => {
    setBannerImageLoading(true);
    try {
      const url = await uploadImage(file);
      setBannerImageUrl(url);
      setBannerImage(file);
      message.success('Banner image uploaded successfully');
    } catch (error) {
      message.error('Failed to upload banner image');
    } finally {
      setBannerImageLoading(false);
    }
    return false; // Prevent default upload behavior
  };

  // Handle location selection from Google Maps
  const handlePlaceSelected = async (place) => {
    try {
      console.log('Place selected:', place);
      
      // Update the form with the selected place
      form.setFieldsValue({ 
        location: place.formatted_address 
      });
      
      // Get coordinates for the selected place
      if (place.formatted_address) {
        const coords = await getEventLoc(place.formatted_address);
        if (coords && coords.latitude && coords.longitude) {
          console.log('Coordinates received:', coords);
          setLocationCoordinates({
            latitude: coords.latitude,
            longitude: coords.longitude
          });
        }
      }
    } catch (error) {
      console.error('Error handling place selection:', error);
      message.error('Failed to get location coordinates');
    }
  };

  // Create the organization
  const createOrganization = async (values) => {
    setSubmitLoading(true);
    try {
      // Extract the email string from the loggedInEmail object
      const ownerEmail = loggedInEmail?.data || '';
      
      const orgData = {
        name: values.name,
        description: values.description || '',
        location: values.location || '',
        owner: ownerEmail, // Now using the extracted email string
        profilePicture: profileImageUrl,
        bannerPicture: bannerImageUrl
      };

      // Add coordinates if available
      if (locationCoordinates) {
        orgData.latitude = locationCoordinates.latitude;
        orgData.longitude = locationCoordinates.longitude;
      }

      const response = await axios.post(`${api}organizations/`, orgData, {
        headers: {
          'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });

      if (response.status === 201) {
        // Organization created successfully
        const newOrg = response.data.data;
        
        // Track organization creation
        analytics.trackOrganizationCreation(
          newOrg._id, 
          newOrg.name, 
          {
            location: newOrg.location,
            hasProfilePicture: !!profileImageUrl,
            hasBannerPicture: !!bannerImageUrl
          }
        );
        
        message.success('Organization created successfully!');
        
        // Update selected organization in the app context
        setAppData(prevData => ({
          ...prevData,
          selectedOrganization: newOrg.name,
          selectedOrganizationInfo: newOrg,
          managedOrganizations: [...(prevData.managedOrganizations || []), newOrg]
        }));
        
        // Switch to the new organization in the backend
        await axios.post(`${api}switchSelectedOrganization/`, {
          org: newOrg.name
        }, {
          withCredentials: true,
          headers: {
            'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
          }
        });
        
        // Navigate to the organizer home page
        navigate('/organization');
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      
      // Handle specific errors
      if (error.response?.data?.error === 'NAME_EXISTS') {
        form.setFields([
          {
            name: 'name',
            errors: ['This organization name already exists. Please choose a different name.']
          }
        ]);
      } else {
        // Track general error
        analytics.trackError('organization_creation_error', error.message || 'Failed to create organization');
        message.error('Failed to create organization. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmit = (values) => {
    createOrganization(values);
  };

  return (
    <>
      <div className="wrapper">
        <Navbar />
        <div className="create-org-container">
          <Card className="create-org-card">
            <Title level={2} className="create-org-title">Create New Organization</Title>
            <Text type="secondary" className="create-org-subtitle">
              Fill in the details below to create your new organization
            </Text>
            
            
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              requiredMark={false}
              className="create-org-form"
              
            >
              <Form.Item
                name="name"
                label="Organization Name"
                rules={[
                  {
                    required: true,
                    message: 'Please enter your organization name',
                  },
                ]}
              >
                <Input placeholder="Enter your organization name" />
              </Form.Item>
              
              <Form.Item
                name="description"
                label="Description"
                extra="Provide a brief description of your organization"
              >
                <TextArea
                  placeholder="Enter organization description"
                  autoSize={{ minRows: 3, maxRows: 6 }}
                />
              </Form.Item>
              
              <Form.Item
                name="location"
                label="Location"
                extra="Where is your organization located?"
              >
                <LocationSearchInput
                  onPlaceSelected={handlePlaceSelected}
                  placeholder="Enter your organization's location"
                />
              </Form.Item>
              
              <Space direction="vertical" size="large" className="upload-container">
                <Form.Item
                  label="Profile Image"
                  extra="This will be displayed as your organization's icon (Square image recommended)"
                >
                  <Upload
                    name="profileImage"
                    listType="picture-card"
                    className="profile-image-uploader"
                    showUploadList={false}
                    beforeUpload={handleProfileImageUpload}
                    accept="image/*"
                  >
                    {profileImageUrl ? (
                      <img
                        src={profileImageUrl}
                        alt="Profile"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div className="upload-button">
                        {profileImageLoading ? 'Uploading...' : (
                          <>
                            <PictureOutlined />
                            <div style={{ marginTop: 8 }}>Upload</div>
                          </>
                        )}
                      </div>
                    )}
                  </Upload>
                </Form.Item>
                
                <Form.Item
                  label="Banner Image"
                  extra="This will be displayed at the top of your organization page (Recommended size: 1200 x 300px)"
                >
                  <Upload
                    name="bannerImage"
                    listType="picture-card"
                    className="banner-image-uploader"
                    showUploadList={false}
                    beforeUpload={handleBannerImageUpload}
                    accept="image/*"
                  >
                    {bannerImageUrl ? (
                      <img
                        src={bannerImageUrl}
                        alt="Banner"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div className="upload-button">
                        {bannerImageLoading ? 'Uploading...' : (
                          <>
                            <PictureOutlined />
                            <div style={{ marginTop: 8 }}>Upload</div>
                          </>
                        )}
                      </div>
                    )}
                  </Upload>
                </Form.Item>
              </Space>
              
              <Divider />
              
              <Form.Item className="submit-buttons">
                <Space>
                  <Button 
                    type="default" 
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={submitLoading}
                  >
                    Create Organization
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default CreateOrganization; 